import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Textarea } from '@/components/ui/Textarea';

/** The server's own limit on a decision note. */
const MAX_REASON = 255;

export interface RejectLeaveModalProps {
  /** "Ravi Kumar's casual leave, 12–14 Oct" — said back so nobody refuses the wrong one. */
  summary: string;
  isSaving: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

/**
 * Asks why before a leave application is refused.
 *
 * The reason is required here: whoever applied reads it on their own record,
 * and a refusal with no explanation is the one that turns into a phone call to
 * the office. The server keeps it optional, so other screens that refuse without
 * this popup still work.
 */
export function RejectLeaveModal({ summary, isSaving, onConfirm, onClose }: RejectLeaveModalProps) {
  const [reason, setReason] = useState('');
  const [isTouched, setIsTouched] = useState(false);

  const trimmed = reason.trim();
  const error = !isTouched
    ? undefined
    : trimmed.length === 0
      ? 'Say why it is being rejected'
      : trimmed.length > MAX_REASON
        ? `Keep it under ${MAX_REASON} characters`
        : undefined;

  function submit(event: FormEvent) {
    event.preventDefault();
    setIsTouched(true);
    if (trimmed.length === 0 || trimmed.length > MAX_REASON || isSaving) return;
    onConfirm(trimmed);
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Reject leave"
      description={summary}
      size="sm"
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="md"
            type="submit"
            form="reject-leave-form"
            isLoading={isSaving}
            className="bg-rose-600 hover:bg-rose-700 text-white font-bold"
          >
            Reject leave
          </Button>
        </>
      }
    >
      <form id="reject-leave-form" onSubmit={submit} noValidate className="py-1">
        <Textarea
          label="Reason for rejecting"
          required
          rows={4}
          maxLength={MAX_REASON}
          placeholder="e.g. Exams are scheduled that week — please apply for the following week."
          value={reason}
          error={error}
          hint="Shown to the staff member on their leave record."
          onChange={(event) => setReason(event.target.value)}
          onBlur={() => setIsTouched(true)}
        />
      </form>
    </Modal>
  );
}
